import {
  SDK_VERSION,
  type PropBag,
  type SessionContext,
  type WireEvent,
} from "@watchover/shared";
import type { WatchOverConfig } from "./config.js";
import {
  SessionTracker,
  getStoredUserId,
  getVisitorId,
  setStoredUserId,
  initStorage,
  storageKey,
} from "./ids.js";
import { Transport } from "./transport.js";
import { AutoCapture } from "./autocapture.js";
import { ReplayRecorder } from "./replay.js";
import { captureErrors, captureWebVitals } from "./vitals.js";
import { buildContextEvent } from "./context.js";
import { scrubUrl } from "./url.js";
import { setCookie } from "./cookies.js";

const OPTOUT_KEY = "wo_optout";

/** True if the visitor previously opted out on this site. */
function isOptedOut(): boolean {
  try {
    return localStorage.getItem(OPTOUT_KEY) === "1";
  } catch {
    return false;
  }
}

/** True if the browser signals Do-Not-Track or Global-Privacy-Control. */
function dntEnabled(): boolean {
  try {
    const n = navigator as any;
    if (n.globalPrivacyControl === true) return true;
    const dnt = n.doNotTrack ?? (window as any).doNotTrack ?? n.msDoNotTrack;
    return dnt === "1" || dnt === "yes" || dnt === 1;
  } catch {
    return false;
  }
}

/** Best-effort wipe of stored identifiers (cookies + localStorage mirrors). */
function clearIdentity(domain?: string) {
  for (const base of ["wo_vid", "wo_uid", "wo_ses", "wo_attr", "wo_sample", "wo_seq"]) {
    const key = storageKey(base);
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    try {
      setCookie(key, "", { days: -1, domain });
    } catch {
      /* ignore */
    }
  }
}

/**
 * The WatchOver client. Owns identity/session state, wires up the capture
 * subsystems, and exposes the public API (track/identify/page/flush/stop).
 *
 * Sampling is decided once per session and persisted, so a recorded session is
 * recorded end-to-end (no half-captured replays).
 */
export class WatchOverClient {
  private cfg: WatchOverConfig;
  private ctx: SessionContext;
  private session: SessionTracker;
  private transport: Transport;
  private autocapture: AutoCapture | null = null;
  private replay: ReplayRecorder | null = null;
  private detachErrors: (() => void) | null = null;
  private started = false;
  private sampledIn: boolean;
  private cookieDomain?: string;

  constructor(cfg: WatchOverConfig) {
    this.cfg = cfg;
    // Resolve identity storage first: this decides whether visitor/session live
    // in a root-domain cookie (shared across subdomains) or host storage.
    const domain = initStorage({
      crossSubdomain: cfg.crossSubdomain,
      cookieDomain: cfg.cookieDomain || undefined,
      projectKey: cfg.key,
    });
    this.cookieDomain = domain;
    if (cfg.debug) {
      console.log(
        "[watchover] identity scope:",
        domain ? `cookie .${domain} (cross-subdomain)` : "host-only",
      );
    }

    this.session = new SessionTracker(cfg.sessionTimeoutMs);
    this.sampledIn = decideSampling(cfg.sampleRate);

    this.ctx = {
      apiKey: cfg.key,
      sessionId: this.session.sessionId,
      visitorId: getVisitorId(),
      userId: getStoredUserId(),
      sdk: SDK_VERSION,
    };
    this.transport = new Transport(this.ctx, cfg);
  }

  start() {
    if (this.started) return;
    // Consent gate: honour an explicit opt-out and (by default) the browser's
    // Do-Not-Track / Global-Privacy-Control signal. When declined we neither
    // record nor set identifiers.
    if (isOptedOut() || (this.cfg.respectDnt && dntEnabled())) {
      this.log("tracking declined (opt-out / DNT / GPC) — capture disabled");
      return;
    }
    this.started = true;
    if (!this.sampledIn) {
      this.log("session not sampled — capture disabled");
      return;
    }
    this.transport.start();

    // Initial pageview + viewport.
    this.emit({
      t: "pageview",
      ts: Date.now(),
      url: scrubUrl(location.href) ?? location.pathname,
      path: location.pathname,
      referrer: scrubUrl(document.referrer) || undefined,
      title: document.title,
    });

    // One-per-session enrichment: UTM/attribution + device/geo-able facets.
    try {
      this.emit(buildContextEvent(this.cookieDomain));
    } catch (err) {
      this.log("context capture failed", err);
    }

    if (this.cfg.replay) {
      this.replay = new ReplayRecorder(this.cfg, (e) => this.emit(e));
      this.replay.start();
    }
    if (this.cfg.autocapture) {
      this.autocapture = new AutoCapture(
        this.cfg,
        (e) => this.emit(e),
        () => this.replay?.snapshot(),
      );
      this.autocapture.start();
    }
    if (this.cfg.captureVitals) captureWebVitals((e) => this.emit(e));
    if (this.cfg.captureErrors)
      this.detachErrors = captureErrors((e) => this.emit(e));

    this.log("started", { session: this.ctx.sessionId, replay: this.cfg.replay });
  }

  // ── public API ──────────────────────────────────────────────────────────────

  track(name: string, props?: PropBag) {
    if (!name) return;
    this.emit({ t: "custom", ts: Date.now(), name, props });
  }

  identify(userId: string, traits?: PropBag) {
    if (!userId) return;
    this.ctx.userId = userId;
    setStoredUserId(userId);
    this.transport.updateContext({ userId });
    this.emit({ t: "identify", ts: Date.now(), userId, traits });
  }

  page(props?: PropBag) {
    this.emit({
      t: "pageview",
      ts: Date.now(),
      url: scrubUrl(location.href) ?? location.pathname,
      path: location.pathname,
      title: document.title,
    });
    if (props) this.track("$pageview_props", props);
  }

  flush() {
    void this.transport.flush(false);
  }

  getSessionId() {
    return this.ctx.sessionId;
  }
  getVisitorId() {
    return this.ctx.visitorId;
  }

  stop() {
    this.autocapture?.stop();
    this.replay?.stop();
    this.detachErrors?.();
    void this.transport.flush(true);
    this.transport.stop();
    this.started = false;
  }

  /** Opt this visitor out: stop capturing now and persist the choice; also wipe
   *  stored identifiers so nothing lingers. */
  optOut() {
    try {
      localStorage.setItem(OPTOUT_KEY, "1");
    } catch {
      /* ignore */
    }
    if (this.started) this.stop();
    try {
      clearIdentity(this.cookieDomain);
    } catch {
      /* ignore */
    }
    this.log("opted out");
  }

  /** Reverse a prior opt-out. Does not auto-start; call start() after. */
  optIn() {
    try {
      localStorage.removeItem(OPTOUT_KEY);
    } catch {
      /* ignore */
    }
    this.log("opted in");
  }

  // ── internals ────────────────────────────────────────────────────────────────

  private emit(event: WireEvent) {
    // Keep the session alive; rotate if it had gone idle, starting a clean replay.
    const { rotated } = this.session.touch();
    if (rotated) {
      this.ctx.sessionId = this.session.sessionId;
      this.transport.updateContext({ sessionId: this.ctx.sessionId });
      this.replay?.snapshot();
      this.log("session rotated", this.ctx.sessionId);
    }
    this.transport.enqueue(event);
  }

  private log(...args: unknown[]) {
    if (this.cfg.debug) console.log("[watchover]", ...args);
  }
}

/** Stable per-session sampling decision, persisted so replays aren't torn. */
function decideSampling(rate: number): boolean {
  if (rate >= 1) return true;
  if (rate <= 0) return false;
  const KEY = storageKey("wo_sample");
  try {
    const prior = sessionStorage.getItem(KEY);
    if (prior === "1") return true;
    if (prior === "0") return false;
    const decision = Math.random() < rate;
    sessionStorage.setItem(KEY, decision ? "1" : "0");
    return decision;
  } catch {
    return Math.random() < rate;
  }
}
